/**
 * Cart progress bars — single threshold and multi-checkpoint rewards.
 *
 * Two goal modes:
 *   amount   — compares cart.total_price; goals are integers in the SHOP currency's
 *              smallest unit (cents) and the remainder is money-formatted.
 *   quantity — compares cart.item_count; goals are plain item counts.
 *
 * Both elements are server-rendered by their Liquid snippet, so the page is already
 * correct without JavaScript. This script only keeps them in sync with Dawn's
 * `PUB_SUB_EVENTS.cartUpdate` when the surrounding markup is NOT re-rendered by the
 * cart section refresh (e.g. when the bar lives in the announcement bar or header).
 */

// Both the progress bar and the checkpoints bar pull in this file, so a page
// showing the two of them loads it twice. Top-level `const` and `class` would
// throw on that second run; the guard makes the second load a no-op.
if (!window.cartProgressBarLoaded) {
  window.cartProgressBarLoaded = true;

  const CART_PROGRESS_FALLBACK_MONEY_FORMAT = '{{amount}}';

  /** Shopify `money_format` placeholders and their number formatting rules. */
  const CART_PROGRESS_MONEY_RULES = {
    amount: { precision: 2, thousands: ',', decimal: '.' },
    amount_no_decimals: { precision: 0, thousands: ',', decimal: '.' },
    amount_with_comma_separator: { precision: 2, thousands: '.', decimal: ',' },
    amount_no_decimals_with_comma_separator: { precision: 0, thousands: '.', decimal: ',' },
    amount_with_apostrophe_separator: { precision: 2, thousands: "'", decimal: '.' },
    amount_no_decimals_with_space_separator: { precision: 0, thousands: ' ', decimal: '.' },
    amount_with_space_separator: { precision: 2, thousands: ' ', decimal: ',' },
    amount_with_period_and_space_separator: { precision: 2, thousands: ' ', decimal: '.' },
  };

  /** Cart Ajax field backing each goal mode. */
  const CART_PROGRESS_VALUE_KEYS = { quantity: 'item_count', amount: 'total_price' };

  /** Placeholder token -> CSS class applied to the injected <span>. */
  const CART_PROGRESS_TOKEN_CLASSES = {
    amount: 'cart-progress__amount',
    goal: 'cart-progress__goal',
  };

  /**
   * Renders `cents` using the shop `money_format` string, never by hand-concatenating
   * a currency symbol. Falls back to a bare grouped number when the format is unusable.
   */
  function formatCartProgressMoney(cents, moneyFormat) {
    const format = moneyFormat || CART_PROGRESS_FALLBACK_MONEY_FORMAT;
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const rule = CART_PROGRESS_MONEY_RULES[match?.[1]] || CART_PROGRESS_MONEY_RULES.amount;
    const amount = (Number(cents) || 0) / 100;
    const [whole, fraction] = Math.abs(amount).toFixed(rule.precision).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, rule.thousands);
    const body = fraction ? `${grouped}${rule.decimal}${fraction}` : grouped;
    const signed = amount < 0 ? `-${body}` : body;

    return match ? format.replace(match[0], signed) : signed;
  }

  /**
   * Turns "You are [amount] away from [goal]" into text nodes plus highlighted spans.
   * Building nodes (instead of innerHTML) keeps merchant-authored copy inert.
   */
  function buildCartProgressNodes(template, replacements) {
    const nodes = [];
    const pattern = /\[(\w+)\]/g;
    let lastIndex = 0;
    let match = pattern.exec(template);

    while (match !== null) {
      const value = replacements[match[1]];
      if (value !== undefined && value !== null && value !== '') {
        if (match.index > lastIndex) nodes.push(document.createTextNode(template.slice(lastIndex, match.index)));
        const span = document.createElement('span');
        span.className = CART_PROGRESS_TOKEN_CLASSES[match[1]] || CART_PROGRESS_TOKEN_CLASSES.amount;
        span.textContent = value;
        nodes.push(span);
        lastIndex = match.index + match[0].length;
      }
      match = pattern.exec(template);
    }

    if (lastIndex < template.length) nodes.push(document.createTextNode(template.slice(lastIndex)));
    return nodes;
  }

  /** Item count without the lines of `excludedProductId` (the store-wide gift product). */
  function countItemsExcluding(cart, excludedProductId) {
    return (cart.items || [])
      .filter((item) => String(item.product_id) !== excludedProductId)
      .reduce((sum, item) => sum + (item.quantity || 0), 0);
  }

  /** Reads the goal value from a full cart object, or null when the field is missing. */
  function readCartValue(cart, goalType, excludedProductId) {
    const value = cart?.[CART_PROGRESS_VALUE_KEYS[goalType]];
    if (typeof value !== 'number' || !Number.isFinite(value)) return null;
    if (goalType === 'quantity' && excludedProductId) return countItemsExcluding(cart, excludedProductId);
    return value;
  }

  /** `cartData` shape differs per publisher: /cart/change.js has totals, /cart/add.js does not. */
  function readPublishedCartValue(event, goalType, excludedProductId) {
    return readCartValue(event?.cartData, goalType, excludedProductId);
  }

  /** One request serves either mode, so a mixed page costs at most one fetch per element. */
  async function fetchCartValue(goalType, excludedProductId) {
    const cartUrl = window.routes?.cart_url || '/cart';
    try {
      const response = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      return readCartValue(await response.json(), goalType, excludedProductId);
    } catch (error) {
      return null;
    }
  }

  class CartProgressBase extends HTMLElement {
    connectedCallback() {
      this.messageElement = this.querySelector('[data-cart-progress-message]');
      this.trackElement = this.querySelector('[data-cart-progress-track]');
      this.fillElement = this.querySelector('[data-cart-progress-fill]');

      // Degrade silently: the server-rendered state stays on screen untouched.
      if (!this.messageElement || !this.trackElement || !this.fillElement) return;

      this.goalType = this.dataset.goalType === 'quantity' ? 'quantity' : 'amount';
      this.moneyFormat = this.dataset.moneyFormat || CART_PROGRESS_FALLBACK_MONEY_FORMAT;
      this.requestToken = 0;

      if (!this.setup()) return;
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event));
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      this.cartUpdateUnsubscriber = undefined;
    }

    /** Hook for subclasses. Return false to opt out of updates. */
    setup() {
      return true;
    }

    async onCartUpdate(event) {
      const token = (this.requestToken += 1);
      const excludedProductId = this.dataset.excludeProductId || '';
      const published = readPublishedCartValue(event, this.goalType, excludedProductId);
      const value = published === null ? await fetchCartValue(this.goalType, excludedProductId) : published;

      // A newer update landed while we were fetching — drop this stale result.
      if (value === null || token !== this.requestToken) return;
      this.render(value);
    }

    /** @param {number} cartValue */
    render() {
      throw new Error('render() must be implemented');
    }

    /** Quantity goals read as "2 shirts" (unit optional); amount goals use the shop money format. */
    formatValue(value) {
      if (this.goalType !== 'quantity') return formatCartProgressMoney(value, this.moneyFormat);

      const count = Math.max(0, Math.round(value));
      const unit = count === 1 ? this.dataset.unitSingular : this.dataset.unitPlural;
      return unit ? `${count} ${unit}` : String(count);
    }

    /** `data-goal-reached` also drives the tooltip's optional hide-on-success rule. */
    setReached(reached) {
      this.classList.toggle('cart-progress--reached', reached);
      this.dataset.goalReached = reached ? 'true' : 'false';
    }

    updateBar(percent) {
      const clamped = Math.max(0, Math.min(100, Number.isFinite(percent) ? percent : 0));
      this.style.setProperty('--cart-progress-value', `${clamped}%`);
      this.fillElement.style.width = `${clamped}%`;
      this.trackElement.setAttribute('aria-valuenow', String(Math.round(clamped)));
    }

    setMessage(template, replacements) {
      this.messageElement.replaceChildren(...buildCartProgressNodes(template || '', replacements));
      this.trackElement.setAttribute('aria-valuetext', this.messageElement.textContent.trim());
    }
  }

  class CartProgressBar extends CartProgressBase {
    setup() {
      this.threshold = Number(this.dataset.threshold) || 0;
      return this.threshold > 0;
    }

    render(cartValue) {
      const reached = cartValue >= this.threshold;
      const remaining = Math.max(0, this.threshold - cartValue);
      const template = reached ? this.dataset.successMessage : this.dataset.progressMessage;

      this.setReached(reached);
      this.setMessage(template, { amount: this.formatValue(remaining) });
      this.updateBar((cartValue / this.threshold) * 100);
      this.dataset.cartValue = String(cartValue);
    }
  }

  class CartCheckpointsBar extends CartProgressBase {
    setup() {
      this.goals = this.parseGoals();
      this.markers = Array.from(this.querySelectorAll('[data-checkpoint-value]'));
      return this.goals.length > 0;
    }

    parseGoals() {
      try {
        const parsed = JSON.parse(this.dataset.goals || '[]');
        if (!Array.isArray(parsed)) return [];
        return parsed
          .filter((goal) => Number.isFinite(Number(goal?.value)) && Number(goal.value) > 0)
          .map((goal) => ({
            value: Number(goal.value),
            label: String(goal.label || ''),
            message: String(goal.message || ''),
          }))
          .sort((first, second) => first.value - second.value);
      } catch (error) {
        return [];
      }
    }

    render(cartValue) {
      const maxGoal = this.goals[this.goals.length - 1].value;
      const nextGoal = this.goals.find((goal) => goal.value > cartValue);
      const allReached = nextGoal === undefined;
      // A tier's own message wins while it is the next one, so each step reads naturally.
      const template = allReached ? this.dataset.successMessage : nextGoal.message || this.dataset.progressMessage;

      this.setReached(allReached);
      this.setMessage(template, {
        amount: this.formatValue(allReached ? 0 : nextGoal.value - cartValue),
        goal: allReached ? '' : nextGoal.label,
      });
      this.updateBar((cartValue / maxGoal) * 100);
      this.updateMarkers(cartValue);
      this.dataset.cartValue = String(cartValue);
    }

    updateMarkers(cartValue) {
      this.markers.forEach((marker) => {
        const goal = Number(marker.dataset.checkpointValue) || 0;
        const reached = cartValue >= goal;
        marker.classList.toggle('cart-checkpoints__marker--reached', reached);
        marker.setAttribute('data-reached', reached ? 'true' : 'false');
      });
    }
  }

  if (!customElements.get('cart-progress-bar')) {
    customElements.define('cart-progress-bar', CartProgressBar);
  }

  if (!customElements.get('cart-checkpoints-bar')) {
    customElements.define('cart-checkpoints-bar', CartCheckpointsBar);
  }
}
